/**
 * Sentry initialisation for the React app.
 *
 * Set VITE_SENTRY_DSN in Netlify env vars to enable. If the DSN is missing
 * (e.g. local dev, branch preview, or before the project is set up in
 * Sentry's dashboard), this file silently no-ops — the rest of the app
 * doesn't care whether Sentry is wired up.
 *
 * Free-tier scale (5,000 errors/month, 50 session replays, 100 users)
 * comfortably covers everything below £10k MRR. We capture:
 *   - All uncaught JS errors
 *   - All unhandled Promise rejections
 *   - Failed network requests via the BrowserTracing integration
 *   - Session replays — only on errors (sampling=0 in normal sessions
 *     keeps us inside the free tier)
 *
 * Things we DON'T capture:
 *   - Errors we explicitly throw to bubble up to the user (e.g. form
 *     validation). Use Sentry.captureException() manually when you do
 *     want one logged.
 *   - Errors from browser extensions (ResizeObserver loop limit, etc.)
 *     — common false-positive noise, filtered in `ignoreErrors`.
 *   - Errors from disconnected users (offline, AbortError on navigation)
 *     — not actionable, filtered in `beforeSend`.
 */

import * as Sentry from "@sentry/react";

let initialised = false;

export function initSentry() {
  if (initialised) return;
  initialised = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info(
        "[sentry] VITE_SENTRY_DSN not set — error reporting disabled. " +
          "Set it in Netlify env vars to enable.",
      );
    }
    return;
  }

  const release = import.meta.env.VITE_COMMIT_SHA as string | undefined;

  // Environment detection — hostname-based, not build-mode-based.
  //
  // Netlify deploy previews build with the same MODE=production as the main
  // branch deploy, so MODE alone can't tell them apart. The reliable signal
  // is the hostname: anything other than the canonical app.safeandsorted.com
  // (and localhost during dev) is a preview, a branch deploy, or a Netlify
  // checker bot — none of which should pollute the production Sentry
  // environment or trigger production alerts.
  //
  // Symptom this fixes: HS-SAAS-APP-3, where Netlify's headless-Chrome
  // checker hit a deploy-preview URL, the SW failed to register (different
  // headers + scope on the preview subdomain), and Sentry tagged the event
  // as 'production' because MODE was 'production'.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isProductionHost = host === "app.safeandsorted.com";
  const isDev = host === "localhost" || host === "127.0.0.1" || host === "";
  const environment = isProductionHost ? "production" : isDev ? "development" : "preview";

  Sentry.init({
    dsn,
    environment,
    release: release || undefined,

    // Performance: 10% of transactions on the canonical prod domain only.
    // 0 everywhere else (previews, branch deploys, local) so they don't
    // burn the free-tier budget.
    tracesSampleRate: isProductionHost ? 0.1 : 0,

    // Replay sampling: never replay normal sessions, but always replay
    // a session where an error was captured. Stays well inside the
    // 50/month free-tier replay budget for our scale.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy: mask all text inputs and form values by default. The
        // replay shows the user's click path and DOM structure but not
        // the actual content they typed (RAMS body text, accident notes,
        // etc. — could contain Article 9 health data on accident reports).
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    // Filter known false-positive noise that adds nothing actionable
    ignoreErrors: [
      // ResizeObserver — common Chrome/Edge noise, never a real bug
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Browser extensions inject scripts that throw — not our code
      "Non-Error promise rejection captured",
      // User-initiated navigation cancels in-flight fetches
      "AbortError",
      "The user aborted a request",
      // Network blip — not actionable
      "Network request failed",
      "Failed to fetch",
      // iOS Safari's quirky promise rejection on visibility changes
      "TypeError: cancelled",
      // PWA service worker registration failures on deploy previews,
      // bot checkers, or browsers that block SW (private mode, some
      // corporate networks). The app gracefully degrades without the SW
      // — offline support is lost but the app still loads. The SW
      // failure is never actionable from Sentry's side.
      "Failed to register a ServiceWorker",
      "An unknown error occurred when fetching the script",
      "ServiceWorker script evaluation failed",
    ],

    beforeSend(event, hint) {
      // Drop chunk-load errors that happen after a Netlify redeploy:
      // a user has the old index.html cached, clicks a route, app tries
      // to fetch an old asset hash that no longer exists. Not a bug —
      // the user just needs to refresh. We trigger a soft reload in the
      // app's ErrorBoundary for these.
      const err = hint?.originalException;
      const msg = typeof err === "object" && err && "message" in err
        ? String((err as Error).message)
        : "";
      if (/Loading chunk \d+ failed/i.test(msg) || /Failed to fetch dynamically imported module/i.test(msg)) {
        return null;
      }
      return event;
    },
  });
}

/** Tag the current Sentry scope with the signed-in user. Call after auth
 *  resolves. Passing `null` clears the user (call on sign-out).
 *  Email omitted by default — we only need the user id to correlate
 *  reports; storing email here pulls PII into Sentry that we don't need. */
export function setSentryUser(id: string | null) {
  if (!initialised) return;
  if (id) {
    Sentry.setUser({ id });
  } else {
    Sentry.setUser(null);
  }
}

/** Tag the current Sentry scope with the active org. Helps when an error
 *  report comes in — we can immediately see which customer is affected. */
export function setSentryOrg(orgId: string | null, orgName?: string) {
  if (!initialised) return;
  if (orgId) {
    Sentry.setTag("org_id", orgId);
    if (orgName) Sentry.setTag("org_name", orgName);
  } else {
    Sentry.setTag("org_id", "none");
  }
}

/** Manually capture an exception with optional context. Use for caught
 *  errors that you handled but want to see in Sentry anyway. */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!initialised) {
    if (import.meta.env.DEV) console.error("[sentry-disabled]", error, context);
    return;
  }
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
